/**
 * Mock Users API Client
 *
 * Provides stateful mock implementation for testing and development.
 * Maintains an in-memory list of users and simulates API behavior.
 *
 * @module api/UsersApiClient.mock
 */

import type {
  User,
  UserRole,
  UserState,
  ListUsersResponse,
  InviteUserResponse,
  ResendInviteResponse,
  UpdateUserRoleResponse,
  PaginationRequest,
} from '../types';
import type { UsersApiClient } from './UsersApiClient';
import { HttpError } from './http';

// =============================================================================
// Fixture Data
// =============================================================================

/**
 * Generate a deterministic set of example users.
 * Mix of states, roles, and login activity.
 */
function generateFixtureUsers(): User[] {
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const threeMonthsAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

  return [
    // Active admins
    {
      id: 'user-001',
      email: 'alice.admin@acme.com',
      emailVerified: true,
      displayName: 'Alice Admin',
      givenName: 'Alice',
      familyName: 'Admin',
      state: 'USER_STATE_ACTIVE' as UserState,
      role: 'admin' as UserRole,
      createdAt: threeMonthsAgo.toISOString(),
      changedAt: oneDayAgo.toISOString(),
    },
    {
      id: 'user-002',
      email: 'bob.boss@acme.com',
      emailVerified: true,
      displayName: 'Bob Boss',
      givenName: 'Bob',
      familyName: 'Boss',
      state: 'USER_STATE_ACTIVE' as UserState,
      role: 'admin' as UserRole,
      createdAt: threeMonthsAgo.toISOString(),
      changedAt: oneWeekAgo.toISOString(),
    },

    // Active members
    {
      id: 'user-003',
      email: 'charlie.member@acme.com',
      emailVerified: true,
      displayName: 'Charlie Member',
      givenName: 'Charlie',
      familyName: 'Member',
      state: 'USER_STATE_ACTIVE' as UserState,
      role: 'member' as UserRole,
      createdAt: oneMonthAgo.toISOString(),
      changedAt: oneDayAgo.toISOString(),
    },
    {
      id: 'user-004',
      email: 'diana.dev@acme.com',
      emailVerified: true,
      displayName: 'Diana Developer',
      givenName: 'Diana',
      familyName: 'Developer',
      state: 'USER_STATE_ACTIVE' as UserState,
      role: 'member' as UserRole,
      createdAt: oneMonthAgo.toISOString(),
      changedAt: oneWeekAgo.toISOString(),
    },
    {
      id: 'user-005',
      email: 'eve.engineer@acme.com',
      emailVerified: true,
      displayName: 'Eve Engineer',
      givenName: 'Eve',
      familyName: 'Engineer',
      state: 'USER_STATE_ACTIVE' as UserState,
      role: 'member' as UserRole,
      createdAt: oneMonthAgo.toISOString(),
    },
    {
      id: 'user-006',
      email: 'frank.finance@acme.com',
      emailVerified: true,
      displayName: 'Frank Finance',
      givenName: 'Frank',
      familyName: 'Finance',
      state: 'USER_STATE_ACTIVE' as UserState,
      role: 'member' as UserRole,
      createdAt: threeMonthsAgo.toISOString(),
    },

    // Invited users (INITIAL state - haven't logged in yet)
    {
      id: 'user-007',
      email: 'grace.guest@acme.com',
      emailVerified: false,
      displayName: 'grace.guest',
      givenName: 'grace.guest',
      familyName: '',
      state: 'USER_STATE_INITIAL' as UserState,
      role: 'member' as UserRole,
      createdAt: oneDayAgo.toISOString(),
    },
    {
      id: 'user-008',
      email: 'henry.hire@acme.com',
      emailVerified: false,
      displayName: 'henry.hire',
      givenName: 'henry.hire',
      familyName: '',
      state: 'USER_STATE_INITIAL' as UserState,
      role: 'member' as UserRole,
      createdAt: oneWeekAgo.toISOString(),
    },
    {
      id: 'user-009',
      email: 'iris.intern@acme.com',
      emailVerified: false,
      displayName: 'iris.intern',
      givenName: 'iris.intern',
      familyName: '',
      state: 'USER_STATE_INITIAL' as UserState,
      role: 'member' as UserRole,
      createdAt: oneWeekAgo.toISOString(),
    },
    {
      id: 'user-010',
      email: 'jack.junior@acme.com',
      emailVerified: false,
      state: 'USER_STATE_INITIAL' as UserState,
      role: 'admin' as UserRole,
      createdAt: oneDayAgo.toISOString(),
    },

    // Inactive/suspended users
    {
      id: 'user-011',
      email: 'karen.left@acme.com',
      emailVerified: true,
      displayName: 'Karen Left',
      givenName: 'Karen',
      familyName: 'Left',
      state: 'USER_STATE_INACTIVE' as UserState,
      role: 'member' as UserRole,
      createdAt: threeMonthsAgo.toISOString(),
      changedAt: oneMonthAgo.toISOString(),
    },
    {
      id: 'user-012',
      email: 'larry.locked@acme.com',
      emailVerified: true,
      displayName: 'Larry Locked',
      givenName: 'Larry',
      familyName: 'Locked',
      state: 'USER_STATE_LOCKED' as UserState,
      role: 'member' as UserRole,
      createdAt: threeMonthsAgo.toISOString(),
      changedAt: oneWeekAgo.toISOString(),
    },
    {
      id: 'user-013',
      email: 'mary.manager@acme.com',
      emailVerified: true,
      displayName: 'Mary Manager',
      givenName: 'Mary',
      familyName: 'Manager',
      state: 'USER_STATE_ACTIVE' as UserState,
      role: 'member' as UserRole,
      createdAt: oneMonthAgo.toISOString(),
    },
  ];
}

/**
 * Default fixture data for mock users.
 * This is a fresh copy that can be used to reset state.
 */
export const DEFAULT_USERS_FIXTURE: User[] = generateFixtureUsers();

// =============================================================================
// Configuration Types
// =============================================================================

/**
 * Configuration options for MockUsersApiClient.
 */
export interface MockUsersApiClientConfig {
  /**
   * Initial users to populate the mock with.
   * @default DEFAULT_USERS_FIXTURE
   */
  initialUsers?: User[];

  /**
   * Optional delay in milliseconds for all responses.
   * Useful for development mode to simulate network latency.
   * Set to 0 for instant responses in tests.
   * @default 0
   */
  delayMs?: number;

  /**
   * Whether to emit a console warning on first use.
   * @default true
   */
  emitWarning?: boolean;

  /**
   * Whether the caller can manage members in the listed org — the `canManage`
   * flag returned on the list response (authority + entitlement). Drives the
   * proactive invite gate.
   * @default true
   */
  canManage?: boolean;
}

// =============================================================================
// Mock Implementation
// =============================================================================

/**
 * Mock implementation of UsersApiClient for testing and development.
 *
 * Features:
 * - Maintains an in-memory list of users
 * - Supports pagination
 * - Can be configured to simulate errors
 * - Emits console warning on first use (in non-test environments)
 *
 * @example
 * ```typescript
 * // Basic usage with defaults
 * const mockClient = new MockUsersApiClient();
 * const { items } = await mockClient.listUsers('org-123', { page: 1, pageSize: 10 });
 *
 * // Trigger seat limit error on next invite
 * mockClient.triggerSeatLimitError();
 * await mockClient.inviteUser('org-123', 'new@example.com', 'Ada', 'Lovelace', 'member'); // throws
 *
 * // Reset to initial state
 * mockClient.reset();
 * ```
 */
export class MockUsersApiClient implements UsersApiClient {
  private users: User[];
  private delayMs: number;
  private emitWarning: boolean;
  private hasWarnedFirstUse: boolean = false;
  // The `canManage` verdict returned on the list response. Default true so
  // existing admin-centric fixtures keep an enabled invite affordance.
  private canManage: boolean;

  // Error trigger flags
  private nextInviteSeatLimitError: boolean = false;
  private nextInviteDuplicateEmailError: boolean = false;
  private nextInviteOrgNotEntitledError: boolean = false;
  private nextInviteWarning: string | null = null;
  private nextDeleteForbiddenError: boolean = false;
  private nextDeleteNotFoundError: boolean = false;
  private nextRoleForbiddenError: boolean = false;
  private nextRoleWarning: string | null = null;

  // Track last invite timestamps for resend testing
  private lastInviteSent: Map<string, string> = new Map();

  // Track the last orgId each method was called with, for test assertions on
  // the org-scoped path. `undefined` = method not yet called. The orgId is now
  // a mandatory path segment, so a recorded value is always a real org id.
  private lastOrgId: {
    listUsers?: string;
    inviteUser?: string;
    resendInvite?: string;
    deleteUser?: string;
    updateUserRole?: string;
  } = {};

  /**
   * Create a new MockUsersApiClient.
   *
   * @param config - Optional configuration for fixtures and behavior
   */
  constructor(config: MockUsersApiClientConfig = {}) {
    // Deep clone the initial users to prevent external mutation
    this.users = JSON.parse(
      JSON.stringify(config.initialUsers ?? DEFAULT_USERS_FIXTURE)
    );
    this.delayMs = config.delayMs ?? 0;
    this.emitWarning = config.emitWarning ?? true;
    this.canManage = config.canManage ?? true;
  }

  // ===========================================================================
  // Configuration Methods
  // ===========================================================================

  /**
   * Trigger a seat limit error on the next inviteUser call.
   * Error is cleared after being thrown once.
   */
  triggerSeatLimitError(): void {
    this.nextInviteSeatLimitError = true;
  }

  /**
   * Trigger a duplicate email error on the next inviteUser call.
   * Error is cleared after being thrown once.
   */
  triggerDuplicateEmailError(): void {
    this.nextInviteDuplicateEmailError = true;
  }

  /**
   * Trigger an "org not entitled" error on the next inviteUser call (403
   * ORG_NOT_ENTITLED). Error is cleared after being thrown once.
   */
  triggerOrgNotEntitledError(): void {
    this.nextInviteOrgNotEntitledError = true;
  }

  /**
   * Make the next inviteUser call succeed but return a partial-success
   * `warning` (e.g. the admin org-membership gap). Cleared after one use.
   */
  triggerNextInviteWarning(message: string): void {
    this.nextInviteWarning = message;
  }

  /**
   * Set the `canManage` verdict returned on subsequent list responses — the
   * proactive invite gate (authority + entitlement). Persists until changed.
   */
  setCanManage(canManage: boolean): void {
    this.canManage = canManage;
  }

  /**
   * Trigger a 403 Forbidden error on the next deleteUser call (e.g. the caller
   * lacks authority, or a backend guard rail like removing the last admin or
   * yourself). Error is cleared after being thrown once.
   */
  triggerDeleteForbiddenError(): void {
    this.nextDeleteForbiddenError = true;
  }

  /**
   * Trigger a 404 USER_NOT_FOUND error on the next deleteUser call. Error is
   * cleared after being thrown once. (A delete of a user id absent from the
   * in-memory list also yields a 404 without this flag.)
   */
  triggerDeleteNotFoundError(): void {
    this.nextDeleteNotFoundError = true;
  }

  /**
   * Trigger a 403 Forbidden error on the next updateUserRole call. Error is
   * cleared after being thrown once.
   */
  triggerRoleForbiddenError(): void {
    this.nextRoleForbiddenError = true;
  }

  /**
   * Make the next updateUserRole call succeed but return a partial-success
   * `warning` (the org-membership sync gap). Cleared after one use.
   */
  triggerNextRoleWarning(message: string): void {
    this.nextRoleWarning = message;
  }

  /**
   * Reset the mock to its initial state.
   * Clears all users and reloads fixtures, clears error triggers.
   */
  reset(): void {
    this.users = JSON.parse(JSON.stringify(DEFAULT_USERS_FIXTURE));
    this.nextInviteSeatLimitError = false;
    this.nextInviteDuplicateEmailError = false;
    this.nextInviteOrgNotEntitledError = false;
    this.nextDeleteForbiddenError = false;
    this.nextDeleteNotFoundError = false;
    this.nextRoleForbiddenError = false;
    this.nextRoleWarning = null;
    this.canManage = true;
    this.lastInviteSent.clear();
    this.lastOrgId = {};
    // Reset warning flag so next use after reset will warn again
    this.hasWarnedFirstUse = false;
  }

  /**
   * Set the response delay in milliseconds.
   */
  setDelay(ms: number): void {
    this.delayMs = ms;
  }

  /**
   * Get the current list of users (for testing assertions).
   */
  getUsers(): readonly User[] {
    return [...this.users];
  }

  /**
   * Get the last invite sent timestamp for a user (for testing assertions).
   */
  getLastInviteSent(userId: string): string | undefined {
    return this.lastInviteSent.get(userId);
  }

  /**
   * Get the last orgId a given method was called with (for testing assertions
   * on the org-scoped path). Returns `undefined` only when the method has not
   * been called yet — otherwise the mandatory org segment that was passed.
   */
  getLastOrgId(
    method:
      | 'listUsers'
      | 'inviteUser'
      | 'resendInvite'
      | 'deleteUser'
      | 'updateUserRole',
  ): string | undefined {
    return this.lastOrgId[method];
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  /**
   * Apply configured delay.
   */
  private async applyDelay(): Promise<void> {
    if (this.delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, this.delayMs));
    }
  }

  /**
   * Emit console warning on first use.
   */
  private warnFirstUse(): void {
    if (this.emitWarning && !this.hasWarnedFirstUse) {
      console.warn(
        '[MockUsersApiClient] Using mock implementation. ' +
          'User management operations will not persist beyond this session. ' +
          'Set VITE_USE_REAL_API=true and deploy the backend to use real data.'
      );
      this.hasWarnedFirstUse = true;
    }
  }

  /**
   * Generate a unique user ID.
   */
  private generateUserId(): string {
    const maxId = this.users.reduce((max, user) => {
      const num = parseInt(user.id.replace('user-', ''), 10);
      return isNaN(num) ? max : Math.max(max, num);
    }, 0);
    return `user-${String(maxId + 1).padStart(3, '0')}`;
  }

  // ===========================================================================
  // UsersApiClient Implementation
  // ===========================================================================

  /**
   * Returns a paginated slice of the in-memory user list.
   */
  async listUsers(
    orgId: string,
    pagination: PaginationRequest,
  ): Promise<ListUsersResponse> {
    this.warnFirstUse();
    await this.applyDelay();
    this.lastOrgId.listUsers = orgId;

    const { page, pageSize } = pagination;
    const totalCount = this.users.length;
    const totalPages = Math.ceil(totalCount / pageSize);

    // Calculate slice bounds (1-indexed pages)
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalCount);

    // Get the page slice
    const items = this.users.slice(startIndex, endIndex).map((user) => ({ ...user }));

    return {
      items,
      pagination: {
        page,
        pageSize,
        totalCount,
        totalPages,
      },
      canManage: this.canManage,
    };
  }

  /**
   * Adds a new user to the in-memory list with INITIAL state.
   * Returns the newly created user.
   */
  async inviteUser(
    orgId: string,
    email: string,
    firstName: string,
    lastName: string,
    role: UserRole,
  ): Promise<InviteUserResponse> {
    this.warnFirstUse();
    await this.applyDelay();
    this.lastOrgId.inviteUser = orgId;

    // Check for configured errors
    if (this.nextInviteSeatLimitError) {
      this.nextInviteSeatLimitError = false;
      throw new HttpError(403, 'Forbidden', {
        code: 'SEAT_LIMIT_EXCEEDED',
        message: 'Seat limit reached. Contact ModernPath to add more seats.',
        details: {
          currentSeats: this.users.length,
          maxSeats: this.users.length,
        },
      });
    }

    if (this.nextInviteDuplicateEmailError) {
      this.nextInviteDuplicateEmailError = false;
      throw new HttpError(409, 'Conflict', {
        code: 'DUPLICATE_EMAIL',
        message: 'A user with this email already exists in the organization.',
        details: {
          email,
        },
      });
    }

    if (this.nextInviteOrgNotEntitledError) {
      this.nextInviteOrgNotEntitledError = false;
      // Status 422 (deliberately distinct from the 403 "lacks
      // platform.user.admin" case); no details. The UI keys off `code`.
      throw new HttpError(422, 'Unprocessable Entity', {
        code: 'ORG_NOT_ENTITLED',
        message: 'this organization cannot manage platform team members',
      });
    }

    // Check for actual duplicate email
    const existingUser = this.users.find(
      (u) => u.email.toLowerCase() === email.toLowerCase()
    );
    if (existingUser) {
      throw new HttpError(409, 'Conflict', {
        code: 'DUPLICATE_EMAIL',
        message: 'A user with this email already exists in the organization.',
        details: {
          email,
        },
      });
    }

    // Create the new user
    const now = new Date().toISOString();
    const newUser: User = {
      id: this.generateUserId(),
      email,
      emailVerified: false,
      displayName: `${firstName} ${lastName}`.trim(),
      givenName: firstName,
      familyName: lastName,
      state: 'USER_STATE_INITIAL',
      role,
      createdAt: now,
    };

    // Add to the list
    this.users.push(newUser);

    // Track invite sent time
    this.lastInviteSent.set(newUser.id, now);

    // One-shot partial-success warning, if configured.
    const warning = this.nextInviteWarning;
    this.nextInviteWarning = null;

    return {
      user: { ...newUser },
      message: 'Invitation sent successfully',
      ...(warning ? { warning } : {}),
    };
  }

  /**
   * Updates the last invite sent timestamp for the specified user.
   * Throws if user is not found.
   */
  async resendInvite(
    orgId: string,
    userId: string,
  ): Promise<ResendInviteResponse> {
    this.warnFirstUse();
    await this.applyDelay();
    this.lastOrgId.resendInvite = orgId;

    // Find the user
    const user = this.users.find((u) => u.id === userId);
    if (!user) {
      throw new HttpError(404, 'Not Found', {
        code: 'USER_NOT_FOUND',
        message: 'User not found',
        details: {
          userId,
        },
      });
    }

    // Update the last invite sent timestamp
    const now = new Date().toISOString();
    this.lastInviteSent.set(userId, now);

    return {
      success: true,
      message: 'Invitation resent successfully',
    };
  }

  /**
   * Updates the specified user's role in the in-memory list. Throws 404 if the
   * user is not found, 422 CANNOT_MODIFY_SELF is NOT simulated (the mock has no
   * caller identity), and 403 if the forbidden trigger is armed.
   */
  async updateUserRole(
    orgId: string,
    userId: string,
    role: UserRole,
  ): Promise<UpdateUserRoleResponse> {
    this.warnFirstUse();
    await this.applyDelay();
    this.lastOrgId.updateUserRole = orgId;

    if (this.nextRoleForbiddenError) {
      this.nextRoleForbiddenError = false;
      throw new HttpError(403, 'Forbidden', {
        code: 'FORBIDDEN',
        message: "You do not have permission to change this user's role.",
      });
    }

    const user = this.users.find((u) => u.id === userId);
    if (!user) {
      throw new HttpError(404, 'Not Found', {
        code: 'USER_NOT_FOUND',
        message: 'User not found',
        details: { userId },
      });
    }

    user.role = role;
    user.changedAt = new Date().toISOString();

    const warning = this.nextRoleWarning;
    this.nextRoleWarning = null;

    return {
      success: true,
      message: `Role updated to ${role}`,
      ...(warning ? { warning } : {}),
    };
  }

  /**
   * Removes the specified user from the in-memory list. Throws 404 if the user
   * is not found (or if the not-found trigger is armed) and 403 if the
   * forbidden trigger is armed. Mirrors the real backend's 204-on-success
   * contract by resolving with no value.
   */
  async deleteUser(orgId: string, userId: string): Promise<void> {
    this.warnFirstUse();
    await this.applyDelay();
    this.lastOrgId.deleteUser = orgId;

    if (this.nextDeleteForbiddenError) {
      this.nextDeleteForbiddenError = false;
      throw new HttpError(403, 'Forbidden', {
        code: 'FORBIDDEN',
        message: 'You do not have permission to remove this user.',
      });
    }

    if (this.nextDeleteNotFoundError) {
      this.nextDeleteNotFoundError = false;
      throw new HttpError(404, 'Not Found', {
        code: 'USER_NOT_FOUND',
        message: 'User not found',
        details: { userId },
      });
    }

    const index = this.users.findIndex((u) => u.id === userId);
    if (index === -1) {
      throw new HttpError(404, 'Not Found', {
        code: 'USER_NOT_FOUND',
        message: 'User not found',
        details: { userId },
      });
    }

    // Remove the user and any tracked invite timestamp.
    this.users.splice(index, 1);
    this.lastInviteSent.delete(userId);
  }
}

// =============================================================================
// Convenience Factory Functions
// =============================================================================

/**
 * Create a MockUsersApiClient with default fixtures.
 */
export function createMockUsersClient(
  overrides?: Partial<MockUsersApiClientConfig>
): MockUsersApiClient {
  return new MockUsersApiClient(overrides);
}

/**
 * Create a MockUsersApiClient with an empty user list.
 * Useful for testing invite flows from scratch.
 */
export function createEmptyMockUsersClient(
  overrides?: Partial<MockUsersApiClientConfig>
): MockUsersApiClient {
  return new MockUsersApiClient({
    initialUsers: [],
    ...overrides,
  });
}

/**
 * Create a MockUsersApiClient with minimal users.
 * Just 3 users for simpler test scenarios.
 */
export function createMinimalMockUsersClient(
  overrides?: Partial<MockUsersApiClientConfig>
): MockUsersApiClient {
  const minimalUsers: User[] = [
    {
      id: 'user-001',
      email: 'admin@example.com',
      emailVerified: true,
      displayName: 'Admin User',
      givenName: 'Admin',
      familyName: 'User',
      state: 'USER_STATE_ACTIVE',
      role: 'admin',
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'user-002',
      email: 'member@example.com',
      emailVerified: true,
      displayName: 'Member User',
      givenName: 'Member',
      familyName: 'User',
      state: 'USER_STATE_ACTIVE',
      role: 'member',
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'user-003',
      email: 'invited@example.com',
      emailVerified: false,
      displayName: 'invited',
      state: 'USER_STATE_INITIAL',
      role: 'member',
      createdAt: new Date().toISOString(),
    },
  ];

  return new MockUsersApiClient({
    initialUsers: minimalUsers,
    ...overrides,
  });
}
